import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  effect,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TwIconBtnComponent } from '@twist/ui-primitives';

/** Title-row layout for {@link TwPageHeaderTitleComponent}. */
export type TwPageHeaderTitleLayout = 'inline' | 'twoTier';

/**
 * `tw-page-header-title` — title row for {@link
 * import('./tw-page-header.component').TwPageHeaderComponent}.
 *
 * Supersedes the static-only `tw-page-hero-title`. Adds:
 *
 * - **optional leading `iconKey`** — an accent-tinted icon chip in front of
 *   the title (mirrors the prototype's `PHTitle iconKey`). Icon content is
 *   projected via `[slot=icon]` so the component stays icon-system agnostic.
 * - **static OR editable title** — `model title` + `editable`. When editable,
 *   a pencil `tw-icon-btn` enters an inline edit mode: **Enter** commits
 *   (emits `titleSave` + writes the `title` model), **Escape** cancels
 *   (reverts to the last committed value); a dirty "Unsaved" flag shows
 *   while the draft differs, with ✓/✕ confirm/cancel icon-btns. Static
 *   titles render as a plain `<h1>`.
 * - **`title-adornment` projection slot** — outline badges / chips /
 *   live-indicator pulse rendered inline beside the title.
 * - **`actions` projection slot** — right-aligned primary buttons / overflow
 *   (inline layout default; `twoTier` stacks the actions below the title).
 *
 * Styling lives in the parent `tw-page-header.component.scss` via the
 * `.tw-page-header-title*` BEM classes — `ViewEncapsulation.None` lets those
 * rules cross the component boundary.
 *
 * ```html
 * <tw-page-header-title [editable]="true" [(title)]="name" (titleSave)="save($event)">
 *   <tw-icon slot="icon" key="lucide:layers" />
 *   <tw-badge slot="title-adornment" type="environment" status="warning">Draft</tw-badge>
 *   <tw-live-indicator slot="title-adornment" [connected]="live()" />
 *   <button slot="actions" tw-btn variant="primary">Publish</button>
 * </tw-page-header-title>
 * ```
 */
@Component({
  selector: 'tw-page-header-title',
  standalone: true,
  imports: [TwIconBtnComponent],
  templateUrl: './tw-page-header-title.component.html',
  host: {
    '[class]': 'classes()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwPageHeaderTitleComponent {
  /** Two-way title text. Static titles render `title()` inside an `<h1>`. */
  readonly title = model<string>('');

  /** When `true`, the pencil edit affordance + inline editor are available. */
  readonly editable = input<boolean, unknown>(false, { transform: booleanAttribute });

  /** Title-row layout: `inline` (actions right, default) or `twoTier` (stacked). */
  readonly layout = input<TwPageHeaderTitleLayout>('inline');

  /**
   * Emitted on confirm (Enter / ✓) with the trimmed draft. The two-way
   * `title` model is also updated; consumers persist the value and may
   * choose to reject empties (the component fires the trimmed string as-is).
   */
  readonly titleSave = output<string>();

  private readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('titleInput');

  /** Edit-mode state (presentational signals only — no subscriptions). */
  protected readonly editing = signal(false);
  protected readonly draft = signal('');

  /** Dirty while editing and the trimmed draft differs from the committed title. */
  protected readonly dirty = computed(
    () => this.editing() && this.draft().trim() !== this.title().trim(),
  );

  protected readonly classes = computed(() =>
    ['tw-page-header-title', `tw-page-header-title--${this.layout()}`].join(' '),
  );

  constructor() {
    // Focus + select the input when edit mode opens (mirrors the prototype).
    effect(() => {
      if (this.editing()) {
        queueMicrotask(() => {
          const el = this.inputRef()?.nativeElement;
          el?.focus();
          el?.select();
        });
      }
    });
  }

  protected enterEdit(): void {
    if (!this.editable()) return;
    this.draft.set(this.title());
    this.editing.set(true);
  }

  protected onDraftInput(value: string): void {
    this.draft.set(value);
  }

  protected commit(): void {
    const next = this.draft().trim() || this.title();
    this.editing.set(false);
    this.title.set(next);
    this.titleSave.emit(next);
  }

  protected cancel(): void {
    this.draft.set(this.title());
    this.editing.set(false);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
    }
  }
}
